<template>
  <v-dialog persistent scrollable v-model="dialog" max-width="500">
    <v-card>
      <v-card-title class="headline">Import site config mapping</v-card-title>
      <v-divider class="mb-4" />
      <v-card-text class="site-config-container">
        <v-form ref="multiSiteForm" v-if="!isJsonView">
          <v-card>
            <v-card-title>Site names</v-card-title>
            <v-card-text>
              <template v-for="(siteName, index) of siteNamesFieldsList">
                <v-row :key="index + '_row'">
                  <v-col cols="10">
                    <v-text-field
                      dense
                      label="Name"
                      :rules="siteName.name.rules"
                      v-model="siteName.name.value"
                    />
                  </v-col>
                  <v-col cols="2">
                    <v-text-field
                      dense
                      required
                      label="Site"
                      type="number"
                      :rules="[...siteName.site.rules, siteUniqueRule]"
                      v-model="siteName.site.value"
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-combobox
                      clearable
                      dense
                      deletable-chips
                      multiple
                      small-chips
                      label="Company numbers"
                      type="number"
                      :rules="siteName.companyNumbers.rules"
                      v-model="siteName.companyNumbers.value"
                    />
                  </v-col>
                </v-row>
                <v-row :key="index + '_btn'">
                  <v-col class="pt-0 pb-0 text-right" cols="12">
                    <v-btn
                      outlined
                      small
                      color="error"
                      v-if="siteNamesFieldsList.length > 1"
                      @click="handleDeleteSite(index)"
                    >
                      Delete
                    </v-btn>
                  </v-col>
                  <v-col class="pa-0" cols="12">
                    <v-divider
                      class="mt-3 mb-4"
                      v-if="
                        siteNamesFieldsList.length > 1 &&
                        index !== siteNamesFieldsList.length - 1
                      "
                    />
                  </v-col>
                </v-row>
              </template>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn outlined small @click="handleAddSiteName">Add</v-btn>
            </v-card-actions>
          </v-card>
          <v-card class="mt-1">
            <v-card-title>Sites</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="6">
                  <v-select
                    label="Site"
                    type="number"
                    :items="siteItemsList"
                    :rules="multiSiteForm.site.rules"
                    v-model="multiSiteForm.site.value"
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    label="Default Site"
                    type="number"
                    :items="siteItemsList"
                    :rules="multiSiteForm.defaultSite.rules"
                    v-model="multiSiteForm.defaultSite.value"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <v-card class="mt-1">
            <v-card-title>Tags</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-combobox
                    clearable
                    deletable-chips
                    multiple
                    small-chips
                    label="Site tags"
                    :rules="multiSiteForm.siteTags.rules"
                    v-model="multiSiteForm.siteTags.value"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-form>
        <vue-json-editor
          mode="code"
          v-model="multiSiteConfig"
          v-if="isJsonView"
          @json-change="handleJsonChange"
          @has-error="handleJsonError"
        />
        <p class="error--text" v-if="siteJsonErr">{{ siteJsonErr.message }}</p>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-btn
          color="secondary"
          :outlined="!isJsonView"
          :loading="loading"
          v-if="isAdvancedMultiSiteEnabled"
          @click="handleShowJson"
        >
          JSON
        </v-btn>
        <v-spacer />
        <v-btn
          text
          :loading="loading"
          @click="handleClose"
          v-if="!isSettingsRequired"
        >
          Close
        </v-btn>
        <v-btn text color="primary" :loading="loading" @click="handleComplete">
          Continue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash';
import vueJsonEditor from 'vue-json-editor';
import { businessApiV1 } from '@/api/v1';
import { businessApiV2 } from '@/api/v2';

const DefaultSiteNameFieldsMap = {
  name: {
    value: null,
    rules: [(v) => !!v || 'Name is required']
  },
  site: {
    value: null,
    rules: [(v) => !!v || 'Site is required']
  },
  companyNumbers: {
    value: []
  }
};

export default {
  data() {
    return {
      dialog: false,
      loading: false,
      sessionId: null,
      environment: null,
      siteNamesFieldsList: [_.cloneDeep(DefaultSiteNameFieldsMap)],
      multiSiteForm: {
        site: {
          value: null,
          rules: [(v) => !!v || 'Site is required']
        },
        defaultSite: {
          value: null,
          rules: [(v) => !!v || 'Default site is required']
        },
        siteTags: {
          value: []
        }
      },
      isJsonView: false,
      siteJsonErr: null,
      siteUniqueRule: (v) =>
        this.siteNamesFieldsList.filter(({ site: { value } }) => value === v)
          .length === 1 || `Site ${v} already exist`,
      sessionSettings: null
    };
  },
  computed: {
    siteItemsList() {
      return this.siteNamesFieldsList
        .filter(({ site: { value } }) => !!value)
        .map(({ site: { value } }) => value);
    },
    multiSiteConfig: {
      get() {
        return {
          siteNames: this.siteNamesFieldsList.map(
            ({ name, site, companyNumbers }) => ({
              name: name.value,
              site: site.value,
              companyNumbers: companyNumbers.value
            })
          ),
          site: this.multiSiteForm.site.value,
          defaultSite: this.multiSiteForm.defaultSite.value,
          siteTags: this.multiSiteForm.siteTags.value
        };
      },
      set() {}
    },
    isAdvancedMultiSiteEnabled() {
      const advancedMultiSiteToggle =
        this.$store.getters.getFeatureToggleByName('advanced_multisite_config');

      return advancedMultiSiteToggle?.enabled || false;
    },
    isSettingsRequired() {
      return !this.sessionSettings?.preprocessorMultiClinicSite;
    }
  },
  components: {
    vueJsonEditor
  },
  methods: {
    open(environment, sessionId, sessionSettings) {
      this.dialog = true;
      this.environment = environment;
      this.sessionId = sessionId;
      this.sessionSettings = sessionSettings;

      if (this.sessionSettings.preprocessorOptions) {
        this.fillConfig({
          ...this.sessionSettings.preprocessorOptions,
          site: this.sessionSettings.preprocessorMultiClinicSite
        });
      }
    },
    handleClose() {
      this.dialog = false;
      this.loading = false;
    },
    fillConfig({ siteNames, site, defaultSite, siteTags }) {
      this.siteNamesFieldsList = siteNames.map(
        ({ name, site, companyNumbers }) => ({
          name: {
            ..._.cloneDeep(DefaultSiteNameFieldsMap.name),
            value: name
          },
          site: {
            ..._.cloneDeep(DefaultSiteNameFieldsMap.site),
            value: site
          },
          companyNumbers: {
            ..._.cloneDeep(DefaultSiteNameFieldsMap.companyNumbers),
            value: companyNumbers
          }
        })
      );
      this.multiSiteForm.site.value = site;
      this.multiSiteForm.defaultSite.value = defaultSite;
      this.multiSiteForm.siteTags.value = siteTags;
    },
    handleAddSiteName() {
      this.siteNamesFieldsList.push(_.cloneDeep(DefaultSiteNameFieldsMap));
    },
    handleDeleteSite(index) {
      const {
        site: { value }
      } = this.siteNamesFieldsList[index];
      this.siteNamesFieldsList.splice(index, 1);

      if (this.multiSiteForm.site.value === value) {
        this.multiSiteForm.site.value = null;
      }
      if (this.multiSiteForm.defaultSite.value === value) {
        this.multiSiteForm.defaultSite.value = null;
      }
    },
    handleShowJson() {
      this.isJsonView = !this.isJsonView;
    },
    handleJsonChange(config) {
      this.fillConfig(config);

      this.siteJsonErr = null;
    },
    handleJsonError: _.debounce(function (err) {
      this.siteJsonErr = err;
    }, 300),
    async handleComplete() {
      if (this.isJsonView) {
        return this.handleShowJson();
      }

      if (this.$refs.multiSiteForm.validate()) {
        this.loading = true;
        await (
          this.$store.getters.isMatcherApiV2Enabled
            ? businessApiV2
            : businessApiV1
        ).updateSessionSettings(this.environment, this.sessionId, {
          ...this.sessionSettings,
          preprocessorMultiClinicSite: this.multiSiteForm.site.value,
          preprocessorOptions: {
            ...this.sessionSettings.preprocessorOptions,
            ...this.multiSiteConfig
          }
        });
        this.$notify({
          type: 'success',
          title: 'Saved',
          text: 'Settings saved'
        });
        this.loading = false;
        this.handleClose();
      }
    }
  }
};
</script>

<style lang="scss">
.site-config-container {
  min-height: 500px;

  > div {
    height: 100%;
  }

  .jsoneditor-vue {
    height: 100%;

    .jsoneditor {
      border-color: #4ba9d2;
    }

    .jsoneditor-menu {
      background-color: #4ba9d2;
      border-bottom-color: #4ba9d2;
    }

    .jsoneditor-poweredBy,
    .jsoneditor-modes {
      display: none;
    }
  }
}
</style>
